import { Query } from '@/utils/react-query/Queries';

/**
 * Bible Studies module gate + setup state for the current organization.
 *
 * enableBibleStudies is a UI flag ONLY — backend authorization is enforced by
 * RLS/RPCs regardless. Everything returned here is display gating:
 *
 * - enabled: org flag is on → module surfaces/routes are visible.
 * - bibleStudyType: the designated SmallGroupTypes row (purpose='BIBLE_STUDY'),
 *   preferring one that has a teacher roleID configured.
 * - setupNeeded: flag on but no classified type (or none with a teacher role).
 * - isRosteredTeacher: caller is in the type's roleID RolePeople roster
 *   (same Roles-membership check Small Groups uses for type gating).
 * - canManage: 'Manage Bible Studies' or admin → all-studies scope, closure.
 * - canView: flag on AND (rostered teacher OR 'View Bible Studies' OR manage).
 * - canCreate: flag on AND setup OK AND (rostered teacher — self-service — OR
 *   'Create Bible Studies' OR manage). Rostered self-service assigns the
 *   caller as primary teacher; assigning someone else needs canManage.
 * - canViewAllReports: 'View Bible Study Reports' OR manage → org-wide ('all'
 *   scope) report access, matching bible_study_report_auth.
 * - canViewReports: flag on AND (all-scope access OR rostered teacher — own
 *   scope). NOTE: 'View Bible Study Reports' alone grants 'all' but NOT 'own';
 *   roster alone grants 'own' but NOT 'all'.
 */
const useBibleStudies = () => {
    const { data: loggedInUser, isPending: userPending } = Query.people.loggedInUser();
    const { data: organization, isPending: orgPending } = Query.organization.byId({
        id: loggedInUser?.organizationID,
        options: { enabled: !!loggedInUser?.organizationID },
    });
    const { data: smallGroupTypes, isPending: typesPending } = Query.smallGroupTypes.all({});

    const enabled = organization?.enableBibleStudies === true;
    const bibleStudyTypes = (smallGroupTypes || []).filter(t => t.purpose === 'BIBLE_STUDY');
    const bibleStudyType = bibleStudyTypes.find(t => t.roleID) || bibleStudyTypes[0] || null;

    const setupNeeded = enabled && (bibleStudyTypes.length === 0 || !bibleStudyTypes.some(t => t.roleID));

    const isRosteredTeacher = !!bibleStudyType?.roleID && !!loggedInUser?.Roles?.some(r => r.id === bibleStudyType.roleID);
    const canManage = !!loggedInUser?.isAdmin || !!loggedInUser?.can?.('Manage Bible Studies');
    const canView = enabled && (canManage || !!loggedInUser?.can?.('View Bible Studies') || isRosteredTeacher);
    const canCreate = enabled && !setupNeeded && (canManage || !!loggedInUser?.can?.('Create Bible Studies') || isRosteredTeacher);
    const canViewAllReports = canManage || !!loggedInUser?.can?.('View Bible Study Reports');
    const canViewReports = enabled && (canViewAllReports || isRosteredTeacher);

    return {
        enabled,
        loading: userPending || orgPending || typesPending,
        bibleStudyType,
        bibleStudyTypes,
        setupNeeded,
        roleID: bibleStudyType?.roleID || null,
        isRosteredTeacher,
        canManage,
        canView,
        canCreate,
        canViewReports,
        canViewAllReports,
    };
};

export default useBibleStudies;
